import type { ReactNode } from 'react';
import styles from './property-list.module.css';

/**
 * A properties grid (docs/DESIGN_SYSTEM.md "The META GROUP", the Details
 * grid of 2026-10-15; mockup `docs/mockups/item-seats.html`): a `<dl>` of
 * label and value pairs in two columns. The label column is as wide as its
 * longest label, small and muted; the value column takes the rest at body
 * size. A row that has nothing to say is left out by the caller, never
 * rendered empty.
 *
 * Since 2026-10-18 (docs/DESIGN_SYSTEM.md "The person and the instance",
 * rule 3; mockup `docs/mockups/account.html`) the same grid is the row of
 * every surface about an object that is not an item: the person's page and
 * the operator's details. Those rows carry an ACT at their end (a caret
 * that opens the row, or one quiet button), so the list grows a third
 * column when `acts` is set, and `ruled` draws a hairline between rows (the
 * item page's Details grid stays flat). Both are layout; the voice is one.
 *
 * `rhythm` (2026-11-02, the item page's Details grid) gives every row one
 * height: each label and value cell is at least `--property-row-h` tall (28px,
 * or the md chip's height where that is taller), its content centred, and no
 * gap between rows, so a row holding a chip, an avatar or a date stands as
 * tall as its neighbours. A host that draws several lists as one grid sets it
 * on each; the person's page and the operator's details keep the flow.
 */
export function PropertyList({
  children,
  className,
  testId,
  acts = false,
  ruled = false,
  rhythm = false,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
  /** Rows carry an act column at their end (`Property`'s `act`). */
  acts?: boolean;
  /** A hairline between rows; the first row has none, so a heading binds to it. */
  ruled?: boolean;
  /** One height for every row, centred, no gap between rows. */
  rhythm?: boolean;
}) {
  return (
    <dl
      className={[styles.list, className].filter(Boolean).join(' ')}
      data-acts={acts || undefined}
      data-ruled={ruled || undefined}
      data-rhythm={rhythm || undefined}
      data-testid={testId}
    >
      {children}
    </dl>
  );
}

/**
 * One row of a `PropertyList`: the label as `<dt>`, the value as `<dd>`.
 * `quiet` dims the value and aligns its digits (a date, a count): a value
 * that is metadata, not a party. `act` is the row's one act at its end
 * (rendered only in a list with `acts`): a caret `IconButton` that opens
 * the row, or one quiet `Button`; never two, never an accent.
 */
export function Property({
  label,
  quiet = false,
  danger = false,
  testId,
  act,
  children,
}: {
  label: ReactNode;
  quiet?: boolean;
  /** The row that closes a surface (Delete account): the value reads dim. */
  danger?: boolean;
  testId?: string;
  act?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <dt className={styles.label}>{label}</dt>
      <dd
        className={styles.value}
        data-quiet={quiet || undefined}
        data-danger={danger || undefined}
        data-testid={testId}
      >
        {children}
      </dd>
      {act !== undefined && <dd className={styles.act}>{act}</dd>}
    </>
  );
}
